import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { customAPI } from "services/utils/CustomAPI";
import { tooltipStyleProps } from "services/constants/Styles";
import { useHistory } from "react-router-dom";
import useConfirmDialog from "services/hooks/useConfirmDialog";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Grid,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  Close,
  Edit,
  Delete,
  Email,
  Phone,
  Business,
  Place,
  Tag,
  Star,
  Save,
  Cancel,
  Timeline,
  Notifications,
  Add,
  Visibility,
  Link as LinkIcon,
  Twitter,
  Facebook,
  Language,
  Schedule,
  Map,
  CheckCircle,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import ContactTimeline from "./ContactTimeline";
import ReminderForm from "./ReminderForm";
import ViewAllDetailsModal from "./ViewAllDetails";
// import CallInterface from "./CallInterface";
import axios from "axios";
import CompleteReminderDialog from "./CompleteReminderDialog";
import {
  setFrajunPhoneNumber,
  setFrajunContactName,
  setFrajunAutoInitiate,
  setFrajunContactId,
  setVisible,
  setCallCompleteContactId,
} from "services/Redux/userToken";

const emailSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  type: yup.string().required("Type is required"),
});

const phoneSchema = yup.object().shape({
  number: yup.string().required("Phone number is required"),
  type: yup.string().required("Type is required"),
  reference: yup.string(),
});

const fallbackAvatar = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const isValidE164 = (num) => /^\+[1-9]\d{6,14}$/.test((num || '').replace(/\s/g, ''));

export default function ContactDetails({ contact, onClose, onUpdateContact, onEditContact }) {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const [activeTab, setActiveTab] = useState("details");
  const history = useHistory();
  const dispatch = useDispatch();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [showReminderForm, setShowReminderForm] = useState(false);
  // const [showCallInterface, setShowCallInterface] = useState(false);
  const [editingReminder, setEditingReminder] = useState([]);
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [isLoadingReminders, setIsLoadingReminders] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [triggerAddActivity, setTriggerAddActivity] = useState(false);
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();
  const [hoveredReminderId, setHoveredReminderId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    reminder: null,
  });
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [activeCallInProgress, setActiveCallInProgress] = useState(
    Boolean(localStorage.getItem("active_call_uuid"))
  );
  // FIX 1C: refreshKey increments when a call completes for this contact so the
  // ContactTimeline re-fetches even if it was not the active tab at dispatch time.
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const callCompleteContactId = useSelector(
    (s) => s.tokenReducer.callCompleteContactId
  );
  const lastRefreshRef = useRef(0);
  useEffect(() => {
    if (!callCompleteContactId) return;
    const matches =
      callCompleteContactId === contact?.id ||
      Number(callCompleteContactId) === Number(contact?.id) ||
      callCompleteContactId === "unknown";
    if (!matches) return;
    const now = Date.now();
    if (now - lastRefreshRef.current < 3000) return;
    lastRefreshRef.current = now;
    setTimelineRefreshKey((k) => k + 1);
    dispatch(setCallCompleteContactId(null));
  }, [callCompleteContactId, contact?.id, dispatch]);

  useEffect(() => {
    const refreshActiveCall = () => {
      setActiveCallInProgress(Boolean(localStorage.getItem("active_call_uuid")));
    };

    refreshActiveCall();
    window.addEventListener("storage", refreshActiveCall);
    const interval = setInterval(refreshActiveCall, 2500);

    return () => {
      window.removeEventListener("storage", refreshActiveCall);
      clearInterval(interval);
    };
  }, []);

  const prevContactId = useRef();

  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
    formState: { errors: emailErrors },
  } = useForm({
    resolver: yupResolver(emailSchema),
  });

  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    reset: resetPhoneForm,
    formState: { errors: phoneErrors },
  } = useForm({
    resolver: yupResolver(phoneSchema),
  });

  const loadReminders = React.useCallback(
    async (pageNumber = 1) => {
      setIsLoadingReminders(true);
      try {
        const response = await axios.get(`${domain}crm/reminders/`, {
          params: {
            contact_id: contact.id,
            limit: 50,
            sort: "-date",
            page: pageNumber,
          },
          headers: {
            Authorization: `token ${token}`,
          },
        });
        const remindersData = response.data;
        setReminders(remindersData.results);
        setTotalPages(Math.ceil(remindersData.count / 10));
      } catch (error) {
        console.error("Error loading reminders:", error);
      }
      setIsLoadingReminders(false);
    },
    [domain, token, contact.id],
  );

  useEffect(() => {
    loadReminders(page);
  }, [loadReminders, page]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [statusRes, sourceRes] = await Promise.all([
          axios.get(`${domain}crm/contact-status/?page_size=100`, {
            headers: { Authorization: `token ${token}` },
          }),
          axios.get(`${domain}crm/contact-source/?page_size=100`, {
            headers: { Authorization: `token ${token}` },
          }),
        ]);
        setStatusOptions(statusRes.data.results);
        setSourceOptions(sourceRes.data.results);
      } catch (err) {
        console.error("Failed loading dropdowns:", err);
      }
    };
    fetchDropdowns();
  }, [domain, token]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(
        `${domain}crm/crm-members/active/?company_id=${requestCompanyId}`,
        {
          headers: {
            Authorization: `token ${token}`,
          },
        },
      );
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setAgents(data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    }
  }, [domain, token, requestCompanyId]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const mappedReminders = useMemo(() => {
    if (!reminders.length || !agents.length) return reminders;
    return reminders.map((r) => {
      const assigned = agents.find((a) => a.id === r.assigned_to);
      return { ...r, assigned_to_with_avatar: assigned || {} };
    });
  }, [reminders, agents]);

  const assignedAgent = contact.assigned_to
    ? agents.find((agent) => agent.id === contact.assigned_to)
    : null;

  useEffect(() => {
    if (contact?.id) {
      if (contact.id !== prevContactId.current) {
        setActiveTab("details");
        setEditingField(null);
        setEditingItem(null);
        setEditValue("");
        setTriggerAddActivity(false);
      }
      loadReminders();
    }
    prevContactId.current = contact?.id;
  }, [contact, loadReminders]);

  useEffect(() => {
    if (contact?.id && !contact?.first_name) {
      axios
        .get(`${domain}crm/contacts/${contact.id}/`, {
          headers: { Authorization: `token ${token}` },
        })
        .then((res) => {
          const data = res?.data;
          if (data?.id && onUpdateContact) {
            onUpdateContact(data);
          }
        })
        .catch(() => {});
    }
  }, [contact?.id, contact?.first_name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === "reminders") {
      loadReminders();
    }
  }, [activeTab, loadReminders]);

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || "");
    setEditingItem(null);
  };

  const startEditingItem = (type, index, item) => {
    setEditingItem({ type, index, data: { ...item } });
    setEditingField(null);
    if (type === "emails") {
      resetEmailForm(item);
    } else if (type === "phone_numbers") {
      resetPhoneForm(item);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingItem(null);
    setEditValue("");
  };

  const saveEdit = async (formData) => {
    setIsSaving(true);
    let updateData = {};

    if (editingField) {
      updateData = { [editingField]: editValue };
    } else if (editingItem) {
      const newArray = [...(contact[editingItem.type] || [])];
      newArray[editingItem.index] = { ...editingItem.data, ...formData };
      updateData = { [editingItem.type]: newArray };
    } else {
      setIsSaving(false);
      return;
    }

    try {
      const res = await customAPI(
        `${domain}crm/contacts/${contact.id}/`,
        "PATCH",
        updateData,
      );
      const updatedContact = res?.data || res?.responseDetails || res;

      if (updatedContact && updatedContact.id) {
        if (onUpdateContact) {
          onUpdateContact(updatedContact);
        }
      }
      cancelEditing();
    } catch (error) {
      console.error("Error updating contact:", error);
    }
    setIsSaving(false);
  };

  const handleMakePrimary = async (type, indexToMakePrimary) => {
    const currentArray = contact[type] || [];
    const newArray = currentArray.map((item, index) => ({
      ...item,
      primary: index === indexToMakePrimary,
    }));

    setIsSaving(true);
    try {
      await axios.patch(`/api/contacts/${contact.id}`, { [type]: newArray });
      if (onUpdateContact) onUpdateContact();
    } catch (error) {
      console.error(`Error making primary ${type}:`, error);
    }
    setIsSaving(false);
  };

  // const getPrimaryEmail = () =>
  //   contact.emails?.find((e) => e.primary) || contact.emails?.[0];
  // const getPrimaryPhone = () =>
  //   contact.phone_numbers?.find((p) => p.primary) || contact.phone_numbers?.[0];
  const getPrimaryAddress = () =>
    contact.addresses?.find((a) => a.primary) || contact.addresses?.[0];
  const getReminderStatus = (reminderDate) => {
    return new Date(reminderDate) > new Date() ? "pending" : "completed";
  };

  const pendingReminders = reminders.filter(
    (r) => getReminderStatus(r.date) === "pending",
  );

  const generateMapUrl = (address) => {
    if (!address) return null;
    if (address.latitude && address.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`;
    }
    const addressString = [
      address.street,
      address.city,
      address.state,
      address.zip,
      address.country,
    ]
      .filter(Boolean)
      .join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      addressString,
    )}`;
  };

  const handleAddActivityClick = () => {
    setActiveTab("timeline");
    setTriggerAddActivity(true);
  };

  const handleClickToCall = (phoneNumber) => {
    if (activeCallInProgress) {
      toast.warning(
        "A call is already active. Please end the current call before starting a new one."
      );
      return;
    }
    if (!isValidE164(phoneNumber)) return;

    const contactName =
      contact.company || `${contact.first_name} ${contact.last_name}`;

    dispatch(setFrajunContactId(contact.id));
    dispatch(setFrajunPhoneNumber(phoneNumber));
    dispatch(setFrajunContactName(contactName));
    dispatch(setFrajunAutoInitiate(false));
    dispatch(setVisible(true));

    toast.success(`Initiating call to ${contactName}`);
  };

  const handleCallClick = () => {
    const primaryPhone =
      contact.phone_numbers?.find((p) => p.primary) ||
      contact.phone_numbers?.[0] ||
      null;

    if (!primaryPhone) {
      toast.error("No primary phone number found for this contact");
      return;
    }
    handleClickToCall(primaryPhone.number);
  };

  const primaryPhoneObj =
    contact.phone_numbers?.find((p) => p.primary) || contact.phone_numbers?.[0] || null;
  const primaryPhoneNumber = primaryPhoneObj?.number || "";
  const canCallFromOverview =
    Boolean(primaryPhoneNumber && isValidE164(primaryPhoneNumber)) && !activeCallInProgress;

  const handleCompleteReminder = async (reminderId, actionPoint) => {
    setLoading(true);
    try {
      await axios.put(
        `${domain}crm/reminders/${reminderId}/complete/`,
        { action_point: actionPoint },
        {
          headers: {
            Authorization: `token ${token}`,
          },
        },
      );
      loadReminders(); // refresh list
    } catch (err) {
      console.error("Failed to complete reminder:", err);
    } finally {
      setLoading(false);
    }
  };

  const getColor = (status) => {
    switch (status) {
      case "Completed":
        return "primary";
      case "Pending":
        return "warning";
      case "Overdue":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ position: "relative", minHeight: "100%" }}>
      <Card
        sx={{
          mb: 3,
          position: "sticky",
          top: 16,
          borderRadius: 2,
          boxShadow: 3,
          border: 1,
          borderColor: "divider",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <CardHeader
          title="Contact Overview"
          action={
            <Box display="flex" gap={1}>
              <Tooltip title="View full profile">
                <IconButton onClick={() => setShowViewAllModal(true)}>
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  canCallFromOverview
                    ? "Make call"
                    : activeCallInProgress
                    ? "Disabled: active call in progress"
                    : "Disabled: requires +91 number"
                }
              >
                <span>
                  <IconButton
                    onClick={handleCallClick}
                    disabled={!canCallFromOverview}
                  >
                    <Phone color={canCallFromOverview ? "success" : "disabled"} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Activity timeline">
                <IconButton onClick={handleAddActivityClick}>
                  <Timeline />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit contact">
                <IconButton
                  onClick={() => {
                    if (onEditContact) {
                      onEditContact(contact.id);
                    } else {
                      history.push(`/admin/crm/edit/${contact.id}`);
                    }
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
              {contact.first_name?.charAt(0)?.toUpperCase()}
              {contact.last_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {contact.company ||
                  `${contact.first_name || ""} ${contact.last_name || ""}`.trim()}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[contact.position, assignedAgent?.username]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
            {contact.status_name && (
              <Chip
                label={contact.status_name}
                size="small"
                sx={{ backgroundColor: contact.status_color, color: "#fff" }}
              />
            )}
            <Tooltip
              title={
                canCallFromOverview
                  ? "Make call"
                  : activeCallInProgress
                  ? "Disabled: active call in progress"
                  : "Disabled: requires +91 number"
              }
            >
              <span>
                <IconButton
                  onClick={handleCallClick}
                  disabled={!canCallFromOverview}
                  size="small"
                >
                  <Phone
                    color={canCallFromOverview ? "success" : "disabled"}
                    fontSize="small"
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box
            sx={{ borderBottom: 1, borderColor: "divider", display: "flex" }}
          >
            <Button
              onClick={() => setActiveTab("details")}
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: activeTab === "details" ? 2 : 0,
                borderColor: "primary.main",
                color:
                  activeTab === "details" ? "primary.main" : "text.secondary",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              Details
            </Button>
            <Button
              onClick={() => setActiveTab("timeline")}
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: activeTab === "timeline" ? 2 : 0,
                borderColor: "primary.main",
                color:
                  activeTab === "timeline" ? "primary.main" : "text.secondary",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              Timeline
            </Button>
            <Button
              onClick={() => {
                setActiveTab("reminders");
                loadReminders();
              }}
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: activeTab === "reminders" ? 2 : 0,
                borderColor: "primary.main",
                color:
                  activeTab === "reminders" ? "primary.main" : "text.secondary",
                position: "relative",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              Reminders
              {pendingReminders.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "error.main",
                    color: "white",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                  }}
                >
                  {pendingReminders.length}
                </Box>
              )}
            </Button>
          </Box>

          <Box sx={{ p: 3 }}>
            {activeTab === "details" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Contact Header */}
                <Box sx={{ textAlign: "center" }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "primary.main",
                      fontSize: "1.5rem",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    {contact.first_name?.charAt(0)?.toUpperCase()}
                    {contact.last_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {contact.company || contact.first_name}
                  </Typography>

                  {/* Position - Inline Editable */}
                  <Box sx={{ mt: 2 }}>
                    {editingField === "position" ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                      >
                        <TextField
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="small"
                          sx={{ width: 200 }}
                          placeholder="Job title"
                        />
                        <IconButton onClick={saveEdit} disabled={isSaving}>
                          {isSaving ? <CircularProgress size={20} /> : <Save />}
                        </IconButton>
                        <IconButton onClick={cancelEditing}>
                          <Cancel />
                        </IconButton>
                      </Box>
                    ) : contact.position ? (
                      <Typography variant="body2" color="text.secondary">
                        {contact.position}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* Company - Inline Editable */}
                  <Box sx={{ mt: 1 }}>
                    {editingField === "company" ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                      >
                        <TextField
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="small"
                          sx={{ width: 200 }}
                          placeholder="Company name"
                        />
                        <IconButton onClick={saveEdit} disabled={isSaving}>
                          {isSaving ? <CircularProgress size={20} /> : <Save />}
                        </IconButton>
                        <IconButton onClick={cancelEditing}>
                          <Cancel />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.875rem",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                        onClick={() => startEditing("company", contact.company)}
                      >
                        {contact.company || "Add company"}
                        <Edit fontSize="small" sx={{ opacity: 0.5 }} />
                      </Box>
                    )}
                    {assignedAgent && (
                      <Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title="Agent"
                      >
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            avatar={
                              <Avatar
                                src={assignedAgent?.image_url || fallbackAvatar}
                                sx={{ width: 20, height: 20 }}
                              />
                            }
                            label={assignedAgent.username}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Status & Source */}
                <Box display="flex" justifyContent="center" gap={1}>
                  {editingField === "status" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        >
                          {statusOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton onClick={saveEdit} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={20} /> : <Save />}
                      </IconButton>
                      <IconButton onClick={cancelEditing}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          px: 1,
                        },
                      }}
                      onClick={() => startEditing("status", contact.status)}
                    >
                      {contact.status_name && (
                        <Chip
                          label={contact.status_name}
                          sx={{
                            backgroundColor: contact.status_color,
                            color: "#fff",
                          }}
                          size="small"
                        />
                      )}
                      <Edit fontSize="small" sx={{ opacity: 0.6 }} />
                    </Box>
                  )}
                  {editingField === "source" ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        >
                          {sourceOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton onClick={saveEdit} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={20} /> : <Save />}
                      </IconButton>
                      <IconButton onClick={cancelEditing}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          px: 1,
                        },
                      }}
                      onClick={() => startEditing("source", contact.source)} // ✅ pass source id
                    >
                      {contact.source_name && (
                        <Chip
                          label={contact.source_name}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      <Edit fontSize="small" sx={{ opacity: 0.6 }} />
                    </Box>
                  )}
                </Box>

                {/* Contact Information - Two Column Layout */}
                <Grid container spacing={3}>
                  {/* Contact Info Items - Emails */}
                  {contact.emails && contact.emails.length > 0 && (
                    <Grid item xs={12} lg={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Email fontSize="small" />
                          Emails
                        </Typography>
                        {(contact.emails || []).map((email, index) => (
                          <Paper
                            key={`email-${index}`}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              bgcolor: "background.default",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            {editingItem?.type === "emails" &&
                            editingItem?.index === index ? (
                              <Box sx={{ width: "100%" }}>
                                <form onSubmit={handleEmailSubmit(saveEdit)}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                    }}
                                  >
                                    <Controller
                                      name="email"
                                      control={emailControl}
                                      defaultValue={email.email}
                                      render={({ field }) => (
                                        <TextField
                                          {...field}
                                          size="small"
                                          placeholder="Email"
                                          error={!!emailErrors.email}
                                          helperText={
                                            emailErrors.email?.message
                                          }
                                        />
                                      )}
                                    />
                                    <Controller
                                      name="type"
                                      control={emailControl}
                                      defaultValue={email.type}
                                      render={({ field }) => (
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Type</InputLabel>
                                          <Select
                                            {...field}
                                            label="Type"
                                            error={!!emailErrors.type}
                                          >
                                            <MenuItem value="work">
                                              Work
                                            </MenuItem>
                                            <MenuItem value="personal">
                                              Personal
                                            </MenuItem>
                                            <MenuItem value="other">
                                              Other
                                            </MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                    />
                                    <Box display="flex" gap={1}>
                                      <Button
                                        type="submit"
                                        size="small"
                                        variant="contained"
                                        disabled={isSaving}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={cancelEditing}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  </Box>
                                </form>
                              </Box>
                            ) : (
                              <>
                                <Email color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    component="a"
                                    href={`mailto:${email.email}`}
                                    sx={{
                                      color: "primary.main",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                      fontWeight: "medium",
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "50ch",
                                    }}
                                  >
                                    {email.email}
                                  </Typography>
                                  <Box display="flex" gap={0.5} mt={0.5}>
                                    <Chip
                                      label={email.type}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {email.primary && (
                                      <Chip
                                        label="Primary"
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          bgcolor: "success.light",
                                          color: "success.dark",
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Box
                                  sx={{ opacity: 0, "&:hover": { opacity: 1 } }}
                                >
                                  {!email.primary && (
                                    <IconButton
                                      size="small"
                                      title="Make Primary"
                                      onClick={() =>
                                        handleMakePrimary("emails", index)
                                      }
                                    >
                                      <Star fontSize="small" />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    title="Edit Email"
                                    onClick={() =>
                                      startEditingItem("emails", index, email)
                                    }
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Box>
                              </>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Contact Info Items - Phones */}
                  {contact.phone_numbers &&
                    contact.phone_numbers.length > 0 && (
                      <Grid item xs={12} lg={6}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Phone fontSize="small" />
                            Phones
                          </Typography>
                          {(contact.phone_numbers || []).map((phone, index) => (
                            <Paper
                              key={`phone-${index}`}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                bgcolor: "background.default",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                                cursor: "pointer",
                              }}
                            >
                              {editingItem?.type === "phone_numbers" &&
                              editingItem?.index === index ? (
                                <Box sx={{ width: "100%" }}>
                                  <form onSubmit={handlePhoneSubmit(saveEdit)}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                      }}
                                    >
                                      <Controller
                                        name="number"
                                        control={phoneControl}
                                        defaultValue={phone.number}
                                        // onClick={() =>
                                        //   setShowCallInterface(true)
                                        // }
                                        render={({ field }) => (
                                          <TextField
                                            {...field}
                                            size="small"
                                            placeholder="Phone number"
                                            error={!!phoneErrors.number}
                                            helperText={
                                              phoneErrors.number?.message
                                            }
                                          />
                                        )}
                                      />
                                      <Controller
                                        name="reference"
                                        control={phoneControl}
                                        defaultValue={phone.reference}
                                        render={({ field }) => (
                                          <TextField
                                            {...field}
                                            size="small"
                                            placeholder="Reference name (e.g., Peter)"
                                          />
                                        )}
                                      />
                                      <Controller
                                        name="type"
                                        control={phoneControl}
                                        defaultValue={phone.type}
                                        render={({ field }) => (
                                          <FormControl size="small" fullWidth>
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                              {...field}
                                              label="Type"
                                              error={!!phoneErrors.type}
                                            >
                                              <MenuItem value="mobile">
                                                Mobile
                                              </MenuItem>
                                              <MenuItem value="work">
                                                Work
                                              </MenuItem>
                                              <MenuItem value="home">
                                                Home
                                              </MenuItem>
                                              <MenuItem value="other">
                                                Other
                                              </MenuItem>
                                            </Select>
                                          </FormControl>
                                        )}
                                      />
                                      <Box display="flex" gap={1}>
                                        <Button
                                          type="submit"
                                          size="small"
                                          variant="contained"
                                          disabled={isSaving}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={cancelEditing}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    </Box>
                                  </form>
                                </Box>
                              ) : (
                                <>
                                  <Phone color="success" />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      component="a"
                                      href={`tel:${phone.number}`}
                                      sx={{
                                        color: "primary.main",
                                        textDecoration: "none",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                        fontWeight: "medium",
                                        display: "block",
                                      }}
                                    >
                                      {phone.number}
                                    </Typography>
                                    {phone.reference && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {phone.reference}
                                      </Typography>
                                    )}
                                    <Box display="flex" gap={0.5} mt={0.5}>
                                      <Chip
                                        label={phone.type}
                                        size="small"
                                        variant="outlined"
                                      />
                                      {phone.primary && (
                                        <Chip
                                          label="Primary"
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            bgcolor: "success.light",
                                            color: "success.dark",
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Tooltip
                                    title={
                                      activeCallInProgress
                                        ? "Active call in progress"
                                        : !isValidE164(phone.number)
                                        ? "Requires E.164 format (e.g. +91 9876543210)"
                                        : `Call ${phone.number}`
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClickToCall(phone.number);
                                        }}
                                        disabled={
                                          activeCallInProgress ||
                                          !isValidE164(phone.number)
                                        }
                                        aria-label={`Call ${phone.number}`}
                                        sx={{
                                          color: "#1D9E75",
                                          padding: "4px",
                                          ml: 0.5,
                                          "&:hover": { background: "#E1F5EE" },
                                          "&.Mui-disabled": { color: "action.disabled" },
                                        }}
                                      >
                                        <Phone sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Box
                                    sx={{
                                      opacity: 0,
                                      "&:hover": { opacity: 1 },
                                    }}
                                  >
                                    {!phone.primary && (
                                      <IconButton
                                        size="small"
                                        title="Make Primary"
                                        onClick={() =>
                                          handleMakePrimary("phones", index)
                                        }
                                      >
                                        <Star fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      title="Edit Phone"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startEditingItem(
                                          "phone_numbers",
                                          index,
                                          phone,
                                        );
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </Grid>
                    )}

                  {/* Company Info */}
                  {contact.company && (
                    <Grid item xs={12} lg={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Business fontSize="small" />
                          Company
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "background.default",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Business color="secondary" />
                          <Box>
                            <Typography fontWeight="medium">
                              {contact.company}
                            </Typography>
                            {contact.position && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {contact.position}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Address, Social, Tags, Notes */}
                <Box
                  sx={{
                    pt: 3,
                    borderTop: 1,
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  {/* Address */}
                  {getPrimaryAddress() &&
                    Object.values(getPrimaryAddress()).some(
                      (value) => value,
                    ) && (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Place fontSize="small" />
                          Address
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{ p: 2, bgcolor: "background.default" }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{ textTransform: "capitalize", mb: 1 }}
                              >
                                {getPrimaryAddress().type} Address
                              </Typography>
                              {getPrimaryAddress().street && (
                                <Typography variant="body2">
                                  {getPrimaryAddress().street}
                                </Typography>
                              )}
                              <Typography variant="body2">
                                {[
                                  getPrimaryAddress().city,
                                  getPrimaryAddress().state,
                                  getPrimaryAddress().postal_code,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </Typography>
                              {getPrimaryAddress().country && (
                                <Typography variant="body2">
                                  {getPrimaryAddress().country}
                                </Typography>
                              )}
                            </Box>
                            {generateMapUrl(getPrimaryAddress()) && (
                              <IconButton
                                component="a"
                                href={generateMapUrl(getPrimaryAddress())}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View on Google Maps"
                                size="small"
                              >
                                <Map fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    )}

                  {/* Social Media */}
                  {contact && Object.values(contact).some((value) => value) && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Language fontSize="small" />
                        Social & Web
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {contact.website && (
                          <Button
                            component="a"
                            href={contact.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Language />}
                            endIcon={<LinkIcon />}
                            sx={{
                              justifyContent: "flex-start",
                              color: "primary.main",
                              textTransform: "none",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            Website
                          </Button>
                        )}
                        {contact.linkedin && (
                          <Button
                            component="a"
                            href={contact.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LinkIcon />}
                            endIcon={<LinkIcon />}
                            sx={{
                              justifyContent: "flex-start",
                              color: "primary.main",
                              textTransform: "none",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            LinkedIn
                          </Button>
                        )}
                        {contact.twitter && (
                          <Button
                            component="a"
                            href={contact.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Twitter />}
                            endIcon={<LinkIcon />}
                            sx={{
                              justifyContent: "flex-start",
                              color: "primary.main",
                              textTransform: "none",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            Twitter
                          </Button>
                        )}
                        {contact.facebook && (
                          <Button
                            component="a"
                            href={contact.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Facebook />}
                            endIcon={<LinkIcon />}
                            sx={{
                              justifyContent: "flex-start",
                              color: "primary.main",
                              textTransform: "none",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            Facebook
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Tags */}
                  {contact.tags && contact.tags.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Tag fontSize="small" />
                        Tags
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {contact.tags.slice(0, 5).map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {contact.tags.length > 5 && (
                          <Chip
                            label={`+${contact.tags.length - 5} more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Notes Preview */}
                  {contact.notes && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Notes
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {contact.notes}
                      </Typography>
                      {contact.notes.length > 150 && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setShowViewAllModal(true)}
                          sx={{ color: "primary.main", mt: 1 }}
                        >
                          Read more
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {activeTab === "timeline" && (
              <ContactTimeline
                contactId={contact.id}
                triggerAddActivity={triggerAddActivity}
                onAddActivityOpened={() => setTriggerAddActivity(false)}
                refreshKey={timelineRefreshKey}
              />
            )}

            {activeTab === "reminders" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1">Reminders</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setShowReminderForm(true)}
                    sx={{
                      backgroundColor: "#84DEB8",
                      color: "#000",
                      "&:hover": { backgroundColor: "#6cc9a1" },
                    }}
                  >
                    Add Reminder
                  </Button>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {isLoadingReminders ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : reminders.length > 0 ? (
                    mappedReminders.map((reminder) => (
                      <Box
                        key={reminder.id}
                        onMouseEnter={() => setHoveredReminderId(reminder.id)}
                        onMouseLeave={() => setHoveredReminderId(null)}
                        position="relative"
                      >
                        <Paper
                          key={reminder.id}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            ...(reminder.status === "Overdue" && {
                              borderLeft: "3px solid #E24B4A",
                            }),
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box>
                              <Typography fontWeight="medium">
                                {reminder.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                              >
                                {reminder.notes}
                              </Typography>
                              <Box mt={1}></Box>
                              {reminder.action_point &&
                                reminder.action_point.toLowerCase() !==
                                  "none" && (
                                  <Typography variant="body2" color="primary">
                                    <strong>Action Point:</strong>{" "}
                                    {reminder.action_point} - Completed by{" "}
                                    {reminder.modifiedby}
                                  </Typography>
                                )}

                              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                {(() => {
                                  return (
                                    <Tooltip
                                      arrow
                                      slotProps={tooltipStyleProps}
                                      title={format(
                                        new Date(reminder.date),
                                        "MMM d, yyyy h:mm a",
                                      )}
                                    >
                                      <Chip
                                        label={reminder.status}
                                        size="small"
                                        color={getColor(reminder.status)}
                                      />
                                    </Tooltip>
                                  );
                                })()}
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title={"Reminder Type"}
                                >
                                  <Chip
                                    label={reminder.reminder_name.replace(
                                      /_/g,
                                      " ",
                                    )}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                                {reminder.assigned_to && (
                                  <Tooltip
                                    arrow
                                    slotProps={tooltipStyleProps}
                                    title="Reminder Assigned To"
                                  >
                                    <Chip
                                      label={reminder.username}
                                      size="small"
                                      avatar={
                                        <Avatar
                                          src={
                                            reminder.assigned_to_with_avatar
                                              ?.image_url || fallbackAvatar
                                          }
                                          sx={{ width: 20, height: 20 }}
                                        />
                                      }
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            {hoveredReminderId !== reminder.id && (
                              <Box sx={{ textAlign: "right" }}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Schedule
                                    fontSize="small"
                                    sx={{ color: "text.secondary" }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {format(
                                      new Date(reminder.date),
                                      "MMM d, yyyy h:mm a",
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>

                          {/* Edit/Delete Icons on Hover */}
                          {hoveredReminderId === reminder.id && (
                            <Box
                              position="absolute"
                              top={8}
                              right={8}
                              display="flex"
                              gap={0.5}
                              zIndex={1}
                            >
                              <IconButton
                                size="small"
                                title="Edit Reminder"
                                onClick={() => {
                                  setEditingReminder(reminder);
                                  setShowReminderForm(true);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                title="Delete Reminder"
                                onClick={() => {
                                  setShowReminderForm(false);
                                  setEditingReminder(null);
                                  showConfirmDialog({
                                    title: "Delete Reminder",
                                    description: `Are you sure you want to delete "${reminder.title}"?`,
                                    onConfirm: async () => {
                                      try {
                                        await axios.delete(
                                          `${domain}crm/reminders/${reminder.id}/`,
                                          {
                                            headers: {
                                              Authorization: `token ${token}`,
                                            },
                                          },
                                        );
                                        loadReminders();
                                      } catch (err) {
                                        console.error(
                                          "Failed to delete reminder:",
                                          err?.response?.data || err.message,
                                        );
                                      }
                                    },
                                  });
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                              <Tooltip title="Mark as Completed" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setActionDialog({
                                      open: true,
                                      reminder: reminder,
                                    })
                                  }
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}

                          {reminder.status === "Overdue" && (
                            <Box
                              sx={{
                                mt: 1.5,
                                mx: -2,
                                mb: -2,
                                px: 2,
                                py: 0.75,
                                bgcolor: "#FDECEA",
                                color: "#E24B4A",
                                borderTop: 1,
                                borderColor: "divider",
                                borderBottomLeftRadius: 4,
                                borderBottomRightRadius: 4,
                              }}
                            >
                              <Typography variant="caption" fontWeight={500}>
                                Due{" "}
                                {Math.max(
                                  0,
                                  Math.floor(
                                    (Date.now() - new Date(reminder.date)) /
                                      86400000,
                                  ),
                                )}{" "}
                                days ago — action needed
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "text.secondary",
                      }}
                    >
                      <Notifications
                        sx={{ fontSize: 32, mb: 1, color: "text.disabled" }}
                      />
                      <Typography>No reminders set</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowReminderForm(true)}
                        sx={{ mt: 2 }}
                      >
                        Add First Reminder
                      </Button>
                    </Box>
                  )}
                  {reminders.length > 0 && totalPages > 1 && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      rowGap={1}
                      mt={2}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 2 }}
                      >
                        Showing page {page} of {totalPages}
                      </Typography>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, val) => setPage(val)}
                        color="primary"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* OLD CallInterface implementation - commented out */}
      {/* {showCallInterface && (
        <CallInterface
          contact={contact}
          open={showCallInterface}
          onClose={() => setShowCallInterface(false)}
          onCallSaved={() => {
            // Refresh contact data or trigger updates
            if (onUpdateContact) onUpdateContact();
          }}
        />
      )} */}

      {showReminderForm && (
        <ReminderForm
          contactId={contact.id}
          reminder={editingReminder}
          onSave={() => {
            setShowReminderForm(false);
            setEditingReminder(null);
            loadReminders();
          }}
          onCancel={() => {
            setShowReminderForm(false);
            setEditingReminder(null);
          }}
        />
      )}

      {showViewAllModal && (
        <ViewAllDetailsModal
          contact={contact}
          open={showViewAllModal}
          onClose={() => setShowViewAllModal(false)}
        />
      )}

      {actionDialog.open && (
        <CompleteReminderDialog
          open={actionDialog.open}
          reminder={actionDialog.reminder}
          onClose={() => setActionDialog({ open: false, reminder: null })}
          onSubmit={handleCompleteReminder}
          loading={loading}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
}
